import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchModerationState,
  fetchModerationViolation,
  fetchModerationViolations,
  fetchXpTransactions,
  type ModerationViolationDetail,
  type ModerationViolationSummary,
  type PublicationRestriction,
  type XpTransaction,
} from './moderationApi';
import './moderation-xp.css';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function restrictionText(state: PublicationRestriction): string {
  if (state.state === 'manual_hold') return 'Публикация фото и впечатлений ограничена до ручного решения модератора.';
  if (state.state === 'timed' && state.restricted_until) {
    return `Публикация фото и впечатлений ограничена до ${formatDate(state.restricted_until)}.`;
  }
  return 'Ограничений на публикации нет.';
}

function appliedRestrictionText(value: Record<string, unknown>): string {
  if (value.state === 'manual_hold' || value.manual_hold_basis === true) {
    return 'После этого решения публикации были ограничены до ручного решения модератора.';
  }
  const end = typeof value.restricted_until === 'string'
    ? value.restricted_until
    : typeof value.basis_end_at === 'string'
      ? value.basis_end_at
      : null;
  if (value.state === 'timed' || (typeof value.restriction_hours === 'number' && value.restriction_hours > 0)) {
    return end
      ? `После этого решения публикации были ограничены до ${formatDate(end)}.`
      : 'После этого решения было применено временное ограничение публикаций.';
  }
  return 'Это решение не создало отдельного ограничения публикаций.';
}

export function ModerationRestrictionCard({ onOpenDecision }: { onOpenDecision?: (id: string) => void }) {
  const [state, setState] = useState<PublicationRestriction | null>(null);
  const [stateError, setStateError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadState = useCallback(async () => {
    setRefreshing(true);
    setStateError(false);
    try {
      const response = await fetchModerationState();
      setState(response.data);
    } catch {
      setStateError(true);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;

    const firstLoad = async () => {
      setRefreshing(true);
      try {
        const response = await fetchModerationState();
        if (alive) {
          setState(response.data);
          setStateError(false);
        }
      } catch {
        if (alive) setStateError(true);
      } finally {
        if (alive) setRefreshing(false);
      }
    };

    void firstLoad();
    const refresh = () => void loadState();
    window.addEventListener('focus', refresh);
    window.addEventListener('hopper:moderation-updated', refresh);
    return () => {
      alive = false;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('hopper:moderation-updated', refresh);
    };
  }, [loadState]);

  if (stateError) {
    return (
      <section className="profile-reference-card moderation-restriction-card is-error" aria-live="polite">
        <span>Ограничение публикаций</span>
        <strong>Не удалось проверить статус ограничения</strong>
        <p>Это не означает, что ограничение снято. Повторите проверку.</p>
        <button type="button" disabled={refreshing} onClick={() => void loadState()}>
          {refreshing ? 'Проверяем…' : 'Повторить'}
        </button>
      </section>
    );
  }

  if (!state || state.state === 'none') return null;

  return (
    <section className="profile-reference-card moderation-restriction-card" aria-live="polite">
      <span>Ограничение публикаций</span>
      <strong>{restrictionText(state)}</strong>
      <p>Сканирование чеков, посещения, чтение, карта, Пульс и доступные предложения продолжают работать.</p>
      {state.latest_decision_id && (
        <button type="button" onClick={() => onOpenDecision?.(state.latest_decision_id!)}>
          Почему это произошло
        </button>
      )}
    </section>
  );
}

function DecisionDrawer({
  detail,
  loading,
  error,
  onRetry,
  onClose,
}: {
  detail: ModerationViolationDetail | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!loading && !detail && !error) return undefined;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        closeRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [detail, error, loading, onClose]);

  if (!loading && !detail && !error) return null;

  return (
    <div
      className="moderation-drawer-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside ref={drawerRef} className="moderation-decision-drawer" role="dialog" aria-modal="true" aria-label="Решение модерации">
        <button ref={closeRef} className="moderation-drawer-close" type="button" onClick={onClose} aria-label="Закрыть">×</button>
        {loading ? (
          <div className="moderation-drawer-state" role="status">Загружаем решение…</div>
        ) : error ? (
          <div className="moderation-drawer-state is-error" role="alert">
            <strong>{error}</strong>
            <button className="profile-secondary-button" type="button" onClick={onRetry}>Повторить</button>
          </div>
        ) : detail ? (
          <>
            <span className="profile-eyebrow">Решение Хоппера</span>
            <h2>{detail.reason_label}</h2>
            <p>{detail.venue.name ?? 'Место Хоппера'} · {formatDate(detail.confirmed_at)}</p>
            <div className="moderation-decision-xp">
              <div><strong>{detail.global_xp_delta} XP</strong><span>Общий XP</span></div>
              <div><strong>{detail.local_xp_delta} XP</strong><span>XP в заведении</span></div>
            </div>
            <section><strong>Объяснение</strong><p>{detail.public_explanation}</p></section>
            <section><strong>Ограничение после этого решения</strong><p>{appliedRestrictionText(detail.applied_restriction)}</p></section>
            <section><strong>Сейчас</strong><p>{restrictionText(detail.current_restriction)}</p></section>
            {detail.overturn && (
              <section className="moderation-overturn">
                <strong>Решение отменено</strong>
                <p>{detail.overturn.explanation || 'Модерация пересмотрела решение.'}</p>
                <span>XP восстановлен: +{detail.reversal_xp_delta}</span>
              </section>
            )}
          </>
        ) : null}
      </aside>
    </div>
  );
}

export function XpModerationWorkspace({ initialDecisionId = null }: { initialDecisionId?: string | null }) {
  const venueId = new URL(window.location.href).searchParams.get('venue_id')?.trim() || null;
  const [xp, setXp] = useState<XpTransaction[]>([]);
  const [xpCursor, setXpCursor] = useState<string | null>(null);
  const [xpMore, setXpMore] = useState(false);
  const [xpMoreLoading, setXpMoreLoading] = useState(false);
  const [xpMoreError, setXpMoreError] = useState<string | null>(null);
  const [violations, setViolations] = useState<ModerationViolationSummary[]>([]);
  const [violationCursor, setViolationCursor] = useState<string | null>(null);
  const [violationMore, setViolationMore] = useState(false);
  const [violationMoreLoading, setViolationMoreLoading] = useState(false);
  const [violationMoreError, setViolationMoreError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ModerationViolationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const openDecision = useCallback(async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const response = await fetchModerationViolation(id);
      setDetail(response.data);
    } catch {
      setDetailError('Не удалось загрузить решение модерации.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [xpPage, violationPage] = await Promise.all([
        fetchXpTransactions(null, venueId),
        fetchModerationViolations(),
      ]);
      setXp(xpPage.data);
      setXpCursor(xpPage.meta.next_cursor);
      setXpMore(xpPage.meta.has_more);
      setViolations(violationPage.data);
      setViolationCursor(violationPage.meta.next_cursor);
      setViolationMore(violationPage.meta.has_more);
    } catch {
      setError('Не удалось загрузить историю XP и решений.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (initialDecisionId) void openDecision(initialDecisionId); }, [initialDecisionId, openDecision]);

  const moreXp = async () => {
    if (!xpCursor || xpMoreLoading) return;
    setXpMoreLoading(true);
    setXpMoreError(null);
    try {
      const page = await fetchXpTransactions(xpCursor, venueId);
      setXp((current) => [...current, ...page.data]);
      setXpCursor(page.meta.next_cursor);
      setXpMore(page.meta.has_more);
    } catch {
      setXpMoreError('Не удалось загрузить следующую страницу XP.');
    } finally {
      setXpMoreLoading(false);
    }
  };

  const moreViolations = async () => {
    if (!violationCursor || violationMoreLoading) return;
    setViolationMoreLoading(true);
    setViolationMoreError(null);
    try {
      const page = await fetchModerationViolations(violationCursor);
      setViolations((current) => [...current, ...page.data]);
      setViolationCursor(page.meta.next_cursor);
      setViolationMore(page.meta.has_more);
    } catch {
      setViolationMoreError('Не удалось загрузить следующую страницу решений.');
    } finally {
      setViolationMoreLoading(false);
    }
  };

  const closeDecision = useCallback(() => {
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
    setDetailId(null);
  }, []);

  if (loading) return <section className="profile-reference-card">Загружаем историю XP…</section>;

  return (
    <div className="profile-workspace-stack moderation-xp-workspace">
      {error && (
        <div className="profile-reference-flash">
          {error} <button type="button" onClick={() => void load()}>Повторить</button>
        </div>
      )}

      <section className="profile-reference-card">
        <div className="moderation-workspace-heading">
          <div><span className="profile-eyebrow">XP</span><h2>{venueId ? 'История XP этого места' : 'История XP'}</h2></div>
          <div>
            <small>Одна строка = одна операция. Списания не меняют историю посещений.</small>
            {venueId && (
              <button className="profile-xp-history-link" type="button" onClick={() => window.location.assign('/profile?section=xp')}>
                Показать весь XP
              </button>
            )}
          </div>
        </div>

        {xp.length === 0 ? (
          <p>Операций XP пока нет.</p>
        ) : (
          <div className="xp-ledger-list">
            {xp.map((row) => (
              <button
                type="button"
                className="xp-ledger-row"
                key={row.id}
                disabled={!row.decision_id}
                onClick={() => row.decision_id && void openDecision(row.decision_id)}
              >
                <span>
                  <strong className={row.points < 0 ? 'is-negative' : 'is-positive'}>{row.points > 0 ? '+' : ''}{row.points} XP</strong>
                  <small>{row.description}</small>
                </span>
                <span>
                  <small>{row.venue?.name ?? 'Общий XP'}</small>
                  <small>{formatDate(row.occurred_at)}</small>
                </span>
              </button>
            ))}
          </div>
        )}

        {xpMoreError && <div className="moderation-inline-error" role="alert">{xpMoreError}</div>}
        {xpMore && (
          <button className="profile-secondary-button" type="button" disabled={xpMoreLoading} onClick={() => void moreXp()}>
            {xpMoreLoading ? 'Загружаем…' : xpMoreError ? 'Повторить загрузку XP' : 'Показать ещё XP'}
          </button>
        )}
      </section>

      <section className="profile-reference-card">
        <div className="moderation-workspace-heading">
          <div><span className="profile-eyebrow">Модерация</span><h2>История решений</h2></div>
        </div>

        {violations.length === 0 ? (
          <p>Решений модерации пока нет.</p>
        ) : (
          <div className="moderation-history-list">
            {violations.map((item) => (
              <button type="button" key={item.id} onClick={() => void openDecision(item.id)}>
                <span>
                  <strong>{item.reason_label}</strong>
                  <small>{item.venue.name ?? 'Место Хоппера'} · {formatDate(item.confirmed_at)}</small>
                </span>
                <span className={item.status === 'overturned' ? 'is-overturned' : ''}>
                  {item.status === 'overturned' ? 'Отменено' : `−${item.penalty_points} XP`}
                </span>
              </button>
            ))}
          </div>
        )}

        {violationMoreError && <div className="moderation-inline-error" role="alert">{violationMoreError}</div>}
        {violationMore && (
          <button className="profile-secondary-button" type="button" disabled={violationMoreLoading} onClick={() => void moreViolations()}>
            {violationMoreLoading ? 'Загружаем…' : violationMoreError ? 'Повторить загрузку решений' : 'Показать ещё решения'}
          </button>
        )}
      </section>

      <DecisionDrawer
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onRetry={() => detailId && void openDecision(detailId)}
        onClose={closeDecision}
      />
    </div>
  );
}
